import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../core/services/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { WorkforceStoreService } from '../../core/services/workforce-store.service';
import { BusinessGuideService } from '../../core/services/business-guide.service';
import { ChangePasswordModalComponent } from '../modals/change-password-modal/change-password-modal.component';

export interface SidebarNavGroup {
  groupTitle: string;
  items: {
    label: string;
    route: string;
    icon: string;
    badge?: string;
    adminOnly?: boolean;
  }[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [AsyncPipe, RouterModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:keydown)': 'handleKeyboardEvent($event)',
  },
  template: `
    <!-- SIDEBAR RETRÁTIL E OPACA (PADRÃO STOCK.FRONT + WORKFORCE) -->
    <nav
      class="wfm-sidebar"
      aria-label="Navegação Principal Workforce"
      [class.wfm-sidebar--expanded]="isSidebarExpanded()"
      [class.wfm-sidebar--collapsed]="!isSidebarExpanded()">
      <!-- Logo & Identidade WFM -->
      <div
        class="wfm-sidebar__brand"
        role="button"
        tabindex="0"
        aria-label="Ir para Visão Geral"
        (click)="navigate('/home')"
        (keydown.enter)="navigate('/home')"
        (keydown.space)="navigate('/home'); $event.preventDefault()"
        title="Ir para Visão Geral">
        <div class="wfm-sidebar__logo-box" aria-hidden="true">
          <span class="wfm-sidebar__logo-m">M</span>
        </div>
        @if (isSidebarExpanded()) {
          <div class="wfm-sidebar__brand-text">
            <div class="wfm-sidebar__brand-title">
              <span>Work</span><span class="wfm-sidebar__gold">Force</span>
            </div>
            <span class="wfm-sidebar__brand-sub">GRUPO MADERO</span>
          </div>
        }
      </div>

      <!-- Botão Circular Flutuante de Expandir/Recolher -->
      <button
        type="button"
        class="wfm-sidebar__toggle-btn"
        (click)="toggleSidebar()"
        [attr.aria-label]="isSidebarExpanded() ? 'Recolher menu lateral' : 'Expandir menu lateral'"
        [title]="isSidebarExpanded() ? 'Recolher menu lateral (ESC)' : 'Expandir menu lateral'">
        <mat-icon>{{ isSidebarExpanded() ? 'chevron_left' : 'chevron_right' }}</mat-icon>
      </button>

      <!-- Lista de Módulos Agrupados -->
      <div class="wfm-sidebar__menu">
        @for (group of visibleNavGroups(); track group.groupTitle) {
          @if (isSidebarExpanded()) {
            <div class="wfm-sidebar__group-label">{{ group.groupTitle }}</div>
          } @else {
            <hr class="wfm-sidebar__divider" />
          }

          @for (item of group.items; track item.route) {
            <a
              [routerLink]="item.route"
              routerLinkActive="wfm-sidebar__link--active"
              (click)="onNavClick()"
              [attr.aria-label]="item.label"
              class="wfm-sidebar__link"
              [class.wfm-sidebar__link--expanded]="isSidebarExpanded()"
              [class.wfm-sidebar__link--collapsed]="!isSidebarExpanded()">
              <div class="wfm-sidebar__icon-slot" aria-hidden="true">
                <mat-icon>{{ item.icon }}</mat-icon>
              </div>
              @if (isSidebarExpanded()) {
                <span class="wfm-sidebar__label">{{ item.label }}</span>
                @if (item.badge) {
                  <span class="wfm-sidebar__badge">{{ item.badge }}</span>
                }
              } @else {
                <div class="wfm-sidebar__tooltip">{{ item.label }}</div>
              }
            </a>
          }
        }

        <!-- Monitoramento Operacional H001 (KPIs na Base do Menu) -->
        <div class="wfm-sidebar__kpis">
          <div class="wfm-sidebar__kpi-card" [class.wfm-sidebar__kpi-card--expanded]="isSidebarExpanded()">
            @if (isSidebarExpanded()) {
              <div class="wfm-sidebar__kpi-head">
                <span class="wfm-sidebar__kpi-title">EFICIÊNCIA DA ESCALA</span>
                <strong class="wfm-sidebar__kpi-val">{{ store.averageEfficiencyScore() }}%</strong>
              </div>
              <div class="wfm-sidebar__kpi-bar">
                <div class="wfm-sidebar__kpi-fill" [style.width.%]="store.averageEfficiencyScore()"></div>
              </div>
            } @else {
              <div class="wfm-sidebar__kpi-mini" [title]="'Eficiência da escala: ' + store.averageEfficiencyScore() + '%'">
                <span class="wfm-sidebar__kpi-mini-label">WFM</span>
                <strong class="wfm-sidebar__kpi-mini-val">{{ store.averageEfficiencyScore() }}%</strong>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Rodapé do Menu: Guia F1 + Alternar Tema (T) -->
      <div class="wfm-sidebar__footer">
        <button
          type="button"
          class="wfm-sidebar__footer-btn"
          [class.wfm-sidebar__footer-btn--expanded]="isSidebarExpanded()"
          (click)="guideService.toggleGuide()"
          aria-label="Abrir Central de Regras WFM e FAQ (F1)"
          title="Central de Regras WFM & FAQ (F1)">
          <div class="wfm-sidebar__icon-slot" aria-hidden="true">
            <mat-icon style="color: var(--madero-gold);">menu_book</mat-icon>
          </div>
          @if (isSidebarExpanded()) {
            <span class="wfm-sidebar__label">Guia de Regras</span>
            <kbd class="wfm-sidebar__kbd">F1</kbd>
          }
        </button>

        <button
          type="button"
          class="wfm-sidebar__footer-btn"
          [class.wfm-sidebar__footer-btn--expanded]="isSidebarExpanded()"
          (click)="themeService.toggleTheme()"
          aria-label="Alternar Tema Claro ou Escuro (Tecla T)"
          title="Alternar Tema Claro/Escuro (Tecla T)">
          <div class="wfm-sidebar__icon-slot" aria-hidden="true">
            <mat-icon style="color: var(--madero-gold);">
              {{ (themeService.isDarkMode$ | async) ? 'light_mode' : 'dark_mode' }}
            </mat-icon>
          </div>
          @if (isSidebarExpanded()) {
            <span class="wfm-sidebar__label">
              Modo {{ (themeService.isDarkMode$ | async) ? 'Escuro' : 'Claro' }}
            </span>
            <kbd class="wfm-sidebar__kbd">T</kbd>
          }
        </button>

        <!-- Usuário logado: alterar senha e sair -->
        <div
          class="wfm-sidebar__user"
          [class.wfm-sidebar__user--expanded]="isSidebarExpanded()"
          [title]="currentUser()?.name + ' · ' + currentUser()?.role">
          <div class="wfm-sidebar__avatar" aria-hidden="true">{{ userInitials() }}</div>
          @if (isSidebarExpanded()) {
            <div class="wfm-sidebar__user-text">
              <strong>{{ currentUser()?.name }}</strong>
              <small>{{ currentUser()?.role }} · {{ currentUser()?.email }}</small>
            </div>
          }
        </div>

        <button
          type="button"
          class="wfm-sidebar__footer-btn"
          [class.wfm-sidebar__footer-btn--expanded]="isSidebarExpanded()"
          (click)="onChangePassword()"
          aria-label="Alterar senha"
          title="Alterar senha">
          <div class="wfm-sidebar__icon-slot" aria-hidden="true">
            <mat-icon>password</mat-icon>
          </div>
          @if (isSidebarExpanded()) {
            <span class="wfm-sidebar__label">Alterar senha</span>
          }
        </button>

        <button
          type="button"
          class="wfm-sidebar__footer-btn wfm-sidebar__footer-btn--logout"
          [class.wfm-sidebar__footer-btn--expanded]="isSidebarExpanded()"
          (click)="authService.logOut()"
          aria-label="Sair da plataforma"
          title="Sair">
          <div class="wfm-sidebar__icon-slot" aria-hidden="true">
            <mat-icon>logout</mat-icon>
          </div>
          @if (isSidebarExpanded()) {
            <span class="wfm-sidebar__label">Sair</span>
          }
        </button>
      </div>
    </nav>

    <!-- Backdrop Opaco Suave quando o Menu Lateral está Expandido -->
    @if (isSidebarExpanded()) {
      <div class="wfm-sidebar-backdrop" (click)="closeSidebar()"></div>
    }
  `,
})
export class SidebarComponent {
  readonly authService = inject(AuthService);
  readonly themeService = inject(ThemeService);
  readonly store = inject(WorkforceStoreService);
  readonly guideService = inject(BusinessGuideService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  readonly currentUser = this.authService.currentUser;
  readonly userInitials = computed(() => {
    const names = (this.currentUser()?.name || '').trim().split(/\s+/).filter(Boolean);
    if (names.length === 0) return 'W';
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  });

  readonly isSidebarExpanded = signal<boolean>(this.getInitialSidebarState());

  readonly navGroups: SidebarNavGroup[] = [
    {
      groupTitle: 'Escalas & Operação',
      items: [
        { label: 'Visão Geral', route: '/home', icon: 'dashboard' },
        { label: 'Planejamento de Escala', route: '/schedule-planning', icon: 'calendar_month' },
      ],
    },
    {
      groupTitle: 'Cadastros & Demanda',
      items: [
        { label: 'Operações & Regras', route: '/branches', icon: 'storefront', adminOnly: true },
        { label: 'Funcionários & Férias', route: '/employees', icon: 'groups' },
        { label: 'Demanda & Feriados', route: '/demand-history', icon: 'trending_up', adminOnly: true },
      ],
    },
    {
      groupTitle: 'Ponto, BI & Governança',
      items: [
        { label: 'Aderência ao Ponto', route: '/timecard', icon: 'schedule' },
        { label: 'Relatórios & BI', route: '/reports', icon: 'analytics', adminOnly: true },
        { label: 'Administração & Logs', route: '/admin', icon: 'security', adminOnly: true },
      ],
    },
  ];

  readonly visibleNavGroups = computed<SidebarNavGroup[]>(() => {
    const isAdmin = this.authService.isAdminOrRoot();
    const isTimecardOnly = this.authService.isTimecardTeam() && !isAdmin;
    return this.navGroups
      .map((g) => ({
        groupTitle: g.groupTitle,
        items: g.items.filter((item) => {
          if (item.adminOnly && !isAdmin) return false;
          if (isTimecardOnly && item.route === '/schedule-planning') {
            return false;
          }
          return true;
        }),
      }))
      .filter((g) => g.items.length > 0);
  });

  private getInitialSidebarState(): boolean {
    try {
      return localStorage.getItem('wfm_sidebar_expanded') === 'true';
    } catch {
      return false;
    }
  }

  toggleSidebar(): void {
    this.isSidebarExpanded.update((v) => {
      const next = !v;
      try {
        localStorage.setItem('wfm_sidebar_expanded', String(next));
      } catch {}
      return next;
    });
  }

  closeSidebar(): void {
    if (this.isSidebarExpanded()) {
      this.isSidebarExpanded.set(false);
      try {
        localStorage.setItem('wfm_sidebar_expanded', 'false');
      } catch {}
    }
  }

  onNavClick(): void {
    if (this.isSidebarExpanded()) {
      this.closeSidebar();
    }
  }

  onChangePassword(): void {
    this.dialog.open(ChangePasswordModalComponent, {
      width: '400px',
      disableClose: true,
      panelClass: 'custom-dialog-container',
    });
  }

  navigate(route: string): void {
    this.router.navigateByUrl(route);
    this.onNavClick();
  }

  handleKeyboardEvent(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName) || target?.isContentEditable) {
      return;
    }
    if (event.key === 'Escape' && this.isSidebarExpanded()) {
      this.closeSidebar();
      return;
    }
    if ((event.key === 't' || event.key === 'T') && !event.ctrlKey && !event.metaKey && !event.altKey) {
      this.themeService.toggleTheme();
    }
  }
}
